import apiClient from './apiClient';

export interface Patient {
  id: string;
  /**
   * User-facing patient ID (MRN) — the readable code staff search by and
   * quote on invoices. Distinct from `id` (the system UUID, which is what
   * every join/route actually uses). Auto-generated on create; editable.
   * May be null for legacy rows not yet numbered.
   */
  medicalRecordNumber?: string | null;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  gender: 'male' | 'female' | 'other';
  email?: string | null;
  phone: string;
  address?: string;
  bloodType?: string;
  allergies?: string;
  medicalHistory?: string[];
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  profilePhotoUrl?: string;
  socialSecurityNumber?: string;
  maritalStatus?: 'single' | 'married' | 'divorced' | 'widowed' | 'other';
  preferredLanguage?: string;
  occupation?: string;
  employer?: string;
  smokingStatus?: 'never' | 'former' | 'current';
  alcoholUse?: 'none' | 'social' | 'moderate' | 'heavy';
  substanceUse?: string;
  exerciseLevel?: 'sedentary' | 'light' | 'moderate' | 'active' | 'very_active';
  lastVisitDate?: string;
  assignedProviderId?: string;
  /** Joined provider, populated by the backend findOne / findAll relations.
   *  Lets the FE render the "Assigned Provider" chip without a second
   *  /users round-trip. Email and other staff PII deliberately excluded. */
  assignedProvider?: {
    id: string;
    firstName: string;
    lastName: string;
  };
  preferredPharmacy?: string;
  familyHistory?: string[];
  isActive: boolean;
  /** First-run demo patient — excluded from lists/counts server-side;
   *  the patient page shows a demo banner with a delete affordance. */
  isDemo?: boolean;
  /** Derived admission status (joined by the backend for the header badge). */
  isAdmitted?: boolean;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export type Gender = 'male' | 'female' | 'other';

export interface FilterPatientDto {
  page?: number;
  limit?: number;
  orderBy?: string;
  order?: 'ASC' | 'DESC';
  search?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  gender?: Gender;
  bloodType?: string;
  isActive?: boolean;
}

export interface PaginatedResponse<T> {
  success: boolean;
  message?: string;
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
  timestamp: string;
  path?: string;
}

export interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
  timestamp: string;
  path?: string;
}

export interface CreatePatientDto {
  /** Optional on create: omit to auto-generate, or pass an existing file
   *  number to carry over a historical record. */
  medicalRecordNumber?: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  gender: 'male' | 'female' | 'other';
  email?: string | null;
  phone: string;
  address?: string;
  bloodType?: string;
  allergies?: string;
  medicalHistory?: string[];
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  socialSecurityNumber?: string;
  maritalStatus?: 'single' | 'married' | 'divorced' | 'widowed' | 'other';
  preferredLanguage?: string;
  occupation?: string;
  employer?: string;
  smokingStatus?: 'never' | 'former' | 'current';
  alcoholUse?: 'none' | 'social' | 'moderate' | 'heavy';
  substanceUse?: string;
  exerciseLevel?: 'sedentary' | 'light' | 'moderate' | 'active' | 'very_active';
  familyHistory?: string[];
  assignedProviderId?: string;
  preferredPharmacy?: string;
  isActive?: boolean;
  notes?: string;
  /**
   * Manually-set last visit date — used by the "Last visit" inline edit on
   * the patient header for clinics who want to backfill an undocumented
   * walk-in. Computed `lastVisit` from signed records still wins on the
   * patient details surface; this DTO field updates the stored
   * `lastVisitDate` so it persists across reloads.
   */
  lastVisitDate?: string | null;
}

export type UpdatePatientDto = Partial<CreatePatientDto>;

export interface PatientStatistics {
  total: number;
  active: number;
  inactive: number;
  byGender: Array<{ gender: string; count: number }>;
  byBloodType: Array<{ bloodType: string; count: number }>;
}

/**
 * Patient API Service
 * Handles all patient-related API calls
 */
export const patientApi = {
  /**
   * Get paginated list of patients with optional filters
   */
  getPatients: async (filters?: FilterPatientDto): Promise<PaginatedResponse<Patient>> => {
    const params = new URLSearchParams();

    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.orderBy) params.append('orderBy', filters.orderBy);
    if (filters?.order) params.append('order', filters.order);
    if (filters?.search) params.append('search', filters.search);
    if (filters?.firstName) params.append('firstName', filters.firstName);
    if (filters?.lastName) params.append('lastName', filters.lastName);
    if (filters?.email) params.append('email', filters.email);
    if (filters?.phone) params.append('phone', filters.phone);
    if (filters?.gender) params.append('gender', filters.gender);
    if (filters?.bloodType) params.append('bloodType', filters.bloodType);
    if (filters?.isActive !== undefined) params.append('isActive', filters.isActive.toString());

    const response = await apiClient.get<PaginatedResponse<Patient>>(
      `/patients${params.toString() ? `?${params.toString()}` : ''}`
    );
    return response.data;
  },

  /**
   * Get a single patient by ID
   */
  getPatientById: async (id: string): Promise<ApiResponse<Patient>> => {
    const response = await apiClient.get<ApiResponse<Patient>>(`/patients/${id}`);
    return response.data;
  },

  /**
   * Create a new patient
   */
  createPatient: async (patientData: CreatePatientDto): Promise<ApiResponse<Patient>> => {
    const response = await apiClient.post<ApiResponse<Patient>>('/patients', patientData);
    return response.data;
  },

  /**
   * Update an existing patient
   */
  updatePatient: async (id: string, patientData: UpdatePatientDto): Promise<ApiResponse<Patient>> => {
    const response = await apiClient.patch<ApiResponse<Patient>>(`/patients/${id}`, patientData);
    return response.data;
  },

  /**
   * Delete a patient (soft delete)
   */
  deletePatient: async (id: string): Promise<void> => {
    await apiClient.delete(`/patients/${id}`);
  },

  /**
   * Get patient statistics
   */
  getPatientStatistics: async (): Promise<ApiResponse<PatientStatistics>> => {
    const response = await apiClient.get<ApiResponse<PatientStatistics>>('/patients/statistics');
    return response.data;
  },
};

export default patientApi;
